import { Divider, Stack, Flex, Box, Grid, Text, Container } from "@mantine/core";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { getTestList } from "../../../../utils/api-communication/RestApiCli";
import GpaReportHeader from "../common/OpaReportHeader";
import FollowUpSection from "./sections/FollowUpSection";
import BaselineSection from "./sections/BaselineSection";
import NewVfiChart from "../charts/NewVfiChart";
import { calculateAgeForTestFloat, parseDateOnly } from "../../../../utils/CommonUtil";
import {
  buildGpaValues,
  calculateDeviationParameters,
  calculateDeviationProbabilityMaps,
} from "../utils/GpaCalculations";
import DeteriorationLegand from "../common/DeteriorationLegand";
import ReportPrintButton from "../common/ReportPrintButton";

export default function SummaryPage(_props) {
  const componentRefs = useRef([]);

  const { t } = useTranslation();
  const [eye, setEye] = useState(null);
  const [type, setType] = useState(null);
  const [tests, setTests] = useState([]);
  const [name, setName] = useState("");
  const [dob, setDob] = useState("");
  const [patientId, setPatientId] = useState("");
  const [values, setValues] = useState([]);
  const [baselinePdAvarageValues, setBaselinePdAvarageValues] = useState([]);
  const [deviationMaps, setDeviationMaps] = useState([]);
  const [deviationProbabilityMaps, setDeviationProbabilityMaps] = useState([]);
  const [, setReady] = useState(false);
  const [vfis, setVfis] = useState([]);
  const [unavailableCount, setUnavailableCount] = useState(0);

  const addToRefs = (index) => (el) => {
    if (el) {
      componentRefs.current[index] = el;
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const uuid = params.get("uuid");
    if (uuid && uuid !== "") {
      getTestList({ uuid: uuid, ascending: true }, (response) => {
        const tests = response.data?.data ?? null;
        if (!tests) return;
        setTests(tests);
        const test = tests[0];
        setEye(test?.eye ?? null);
        setType(test?.testType ?? null);
        setName(test?.patient.name ?? "");
        setDob(test?.patient.dob ?? "");
        setPatientId(test?.patient.uuid ?? "");
      });
    }
  }, []);

  useEffect(() => {
    if (!tests?.length) return;
    const { values: tempValues, unavailable } = buildGpaValues(tests);
    setUnavailableCount(unavailable);
    setValues(tempValues);
  }, [tests]);

  useEffect(() => {
    const result = calculateDeviationParameters(values);
    if (!result) return;
    setBaselinePdAvarageValues(result.baselinePdAvarageValues);
    setDeviationMaps(result.deviationMaps);
    const tempStatisticMaps = calculateDeviationProbabilityMaps(result.deteriorationMaps);
    setDeviationProbabilityMaps(tempStatisticMaps);
    setReady(true);

    const tempVfis = values.map((val) => {
      return {
        age: calculateAgeForTestFloat(new Date(val.startDate), parseDateOnly(val.patient.dob)),
        vfi: val.vfi,
      };
    });
    setVfis(tempVfis);
  }, [values]);

  const divider = <Divider size={"md"} variant="dashed" color="gray.6" />;
  const headerText = (text) => (
    <Text ta="center" fw={600} size={"lg"} color="dimmed">
      {text}
    </Text>
  );

  return (
    <Stack>
      <Container size={"xl"}>
        <Flex justify={"flex-end"} align={"center"} px={"lg"} pt={"md"}>
          <ReportPrintButton componentRefs={Object.values(componentRefs.current)} />
        </Flex>
        <Box ref={addToRefs(0)}>
          <Box px={"lg"} pb={"lg"} pt={"md"}>
            <GpaReportHeader
              title={"Summary"}
              name={name}
              dob={dob}
              type={type}
              uuid={patientId}
              eye={eye}
            />
            {unavailableCount > 0 && (
              <Text c="red.6">
                {t("OpaReports.metricsUnavailableWarningText", { count: unavailableCount })}
              </Text>
            )}
            <Stack my={"sm"}>
              <Grid>
                <Grid.Col span={3}>{headerText(t("grayTone"))}</Grid.Col>
                <Grid.Col span={3}>{headerText(t("patternDeviation"))}</Grid.Col>
                <Grid.Col span={3}>{headerText(t("grayTone"))}</Grid.Col>
                <Grid.Col span={3}>{headerText(t("patternDeviation"))}</Grid.Col>
              </Grid>
              <Flex>
                <BaselineSection test={values[0]} halfMaps={true} />
                <Divider
                  size={"md"}
                  variant="dashed"
                  color="gray.6"
                  orientation="vertical"
                  mx={"sm"}
                />
                <BaselineSection test={values[1]} halfMaps={true} />
              </Flex>
              {divider}
              <NewVfiChart data={vfis} />
            </Stack>
            {divider}
            <Text ta="left" fw={500} c="dimmed">
              {t("OpaReports.followup")}
            </Text>
            <Grid>
              <Grid.Col span={3}>{headerText(t("grayTone"))}</Grid.Col>
              <Grid.Col span={3}>{headerText(t("patternDeviation"))}</Grid.Col>
              <Grid.Col span={3}>{headerText(t("deviationFromBaseline"))}</Grid.Col>
              <Grid.Col span={3}>{headerText(t("progressionAnalysis"))}</Grid.Col>
            </Grid>
            {
              <FollowUpSection
                test={values[values.length - 1]}
                baselinePdAverages={baselinePdAvarageValues}
                deviation={deviationMaps[deviationMaps.length - 1]}
                deviationProbability={deviationProbabilityMaps[deviationProbabilityMaps.length - 1]}
              />
            }
            {divider}
            <DeteriorationLegand cols={4} my={"sm"} spacing={"xs"} />
          </Box>
        </Box>
      </Container>
    </Stack>
  );
}
